import { Alert, Button, Card, Col, Input, Row, Tag, Form } from 'antd';
import React, { FC, useEffect, useState, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { GroupItem, GroupItemStore, GroupSummaryStore } from 'src/stores/groups-store';
import { AppConfig, container } from 'src/inversify.config';
import { nameof } from 'src/core/utils/object';
import { ENV } from 'src/';
import { AppConfiguration } from 'src/core/services/configuration.service';

import { guid } from 'odata-query';
import { RuleObject } from 'antd/es/form';
import { curateString } from 'src/core/utils/object';

const GroupGeneralPropertyEditCard: FC<{ groupId: string }> = ({ groupId }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const groupStore = container.get(GroupItemStore);
  const groupState = groupStore.state;
  const [error, setError] = useState<string | undefined>('');
  const config: AppConfiguration = useContext(AppConfig);

  const groupSummaryStore = container.get(GroupSummaryStore);

  useMemo(() => {
    form.resetFields();
    setError('');
    groupStore.clearMessages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    form.resetFields();
  }, [groupState.item.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = async () => {
    let item = await form.validateFields();
    item.name = curateString(item.name);

    const groupName = item.name;
    const organizationId = groupState.item.value?.organizationId;
    const groupType = groupState.item.value?.groupType;

    if (groupType) {
      const duplicatedGroups = await groupSummaryStore.getGroups(
        {
          filter: { Name: groupName, OrganizationId: guid(organizationId) },
        },
        [groupType]
      );

      if (duplicatedGroups?.length && groupName !== groupState.item.value?.name) {
        setError(t('ThereIsAlreadyAGroupWithThisName'));
        return;
      }
    }

    const response = await groupStore.save(groupId, item as GroupItem);
    if (response?.isSuccess) {
      groupState.set((s) => {
        s.item.name = curateString(item.name);
        return s;
      });
      form.resetFields();
      setError('');
      groupStore.clearMessages();
    } else {
      const msg = config.environment === ENV.DEV ? groupState.errorMessage.value : t('ErrorSavingGroupProperties');
      setError(msg);
    }
  };

  const onReset = () => {
    form.resetFields();
  };

  return (
    <Card title={<h3 style={{ textAlign: 'left', marginLeft: 16, textWrap: 'wrap' }}>{t('GeneralProperties')}</h3>}>
      {(error || '').length !== 0 && <Alert type="error" message={t(error || '')} />}
      <Form initialValues={groupState.item.value} form={form} layout="vertical" autoComplete="off" style={{ margin: '16px 40px 40px 40px' }}>
        <Form.Item
          name={nameof<GroupItem>('name')}
          label={t('Name')}
          rules={[
            { required: true, message: t('PleaseEnterName') },
            () => ({
              validator(_, value: string) {
                if (value && value.trim().length === 0) return Promise.reject(t('PleaseEnterName'));
                return Promise.resolve();
              },
            }),
            () => ({
              validator: (ruleObj: RuleObject, value: string) => {
                const maxLenght = 128;
                if (value === undefined) return Promise.resolve();
                if (value.length > maxLenght) {
                  return Promise.reject(t('TextFieldLimitError').replace('{0}', t('Name')).replace('{1}', `${maxLenght}`));
                } else return Promise.resolve();
              },
            }),
          ]}
        >
          <Input autoComplete="off" allowClear placeholder={t('Name')} />
        </Form.Item>
        <Form.Item label={t('Organization')}>
          <Tag>{groupState.value.item?.organization}</Tag>
        </Form.Item>
        <Form.Item>
          <Row gutter={[16, 8]}>
            <Col lg={12} xs={24} style={{ minWidth: 80 }}>
              <Button htmlType="button" type="default" disabled={groupState.isBusy.value} onClick={onReset} style={{ width: '100%' }}>
                {t('Reset')}
              </Button>
            </Col>
            <Col lg={12} xs={24} style={{ minWidth: 130 }}>
              <Button
                type="primary"
                disabled={groupState.isBusy.value}
                onClick={onSave}
                style={{ width: '100%', height: 'auto', minHeight: '60px', textWrap: 'wrap' }}
              >
                {t('SaveChanges')}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default GroupGeneralPropertyEditCard;
